/**
 * Cliente HTTP base.
 *
 * Funcionalidades:
 * - Toda resposta de sucesso vem em ApiResponse<T> { success, message, data }.
 * - Injeta Authorization: Bearer <token> automaticamente.
 * - Renova a sessão sozinho: antes de a validade do access token acabar (proativo) e
 *   ao receber 401 (reativo, com uma única repetição da request original).
 * - Timeout configurável por request (padrão 15s).
 * - Retry automático para erros de rede/timeout (máx. 2 tentativas).
 * - Detecção de indisponibilidade da rede (navigator.onLine).
 * - Lança ApiError com código semântico (NETWORK, TIMEOUT, SERVER, etc.).
 * - Encerra a sessão apenas quando a API recusa a renovação — nunca por falha de rede.
 * - No Capacitor (Android/iOS) usa CapacitorHttp nativo para bypassar CORS.
 */

import { ApiError, type ApiErrorCode, type ApiResponse } from "@/types/api"
import { token } from "@/lib/tokenStore"
import { endSession, refreshSession } from "@/lib/session"
import { CapacitorHttp } from "@capacitor/core"
import { API_BASE_URL as BASE_URL } from "@/lib/apiConfig"
import {
  NATIVE_PLATFORM,
  PLATFORM_HEADER,
  REFRESH_TOKEN_HEADER,
  isNativeApp,
} from "@/lib/platform"

/** Tempo padrão de timeout em ms */
const DEFAULT_TIMEOUT_MS = 15_000

/** Máximo de retries automáticos para erros de rede/timeout */
const MAX_RETRIES = 2

/** Delay base entre retries (dobra a cada tentativa — exponential backoff) */
const RETRY_BASE_MS = 800

/**
 * Margem para a renovação proativa: com o token vencendo dentro desse prazo, renova
 * antes de enviar. Cobre a latência da própria request e o relógio fora de sincronia
 * entre cliente e servidor, evitando um 401 previsível.
 */
const RENEW_BEFORE_EXPIRY_SECONDS = 30

type RequestOptions = RequestInit & {
  /** Se true, não injeta o header Authorization */
  public?: boolean
  /** Timeout em ms (padrão 15000) */
  timeout?: number
  /** Quantidade de retries automáticos para erros de rede (padrão MAX_RETRIES) */
  retries?: number
  /** Se true, um 401 não dispara tentativa de renovação de sessão */
  skipAuthRefresh?: boolean
  /** Reject retries/refresh dispatch after the captured owner session changes. */
  sessionGuard?: () => void
}

// ─── helpers internos ───────────────────────────────────────────────────────

function isDisconnected(): boolean {
  return typeof navigator !== "undefined" && !navigator.onLine
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

/** Mapeia HTTP status para ApiErrorCode */
function statusToCode(status: number): ApiErrorCode {
  if (status === 401) return "UNAUTHORIZED"
  if (status === 403) return "FORBIDDEN"
  if (status === 404) return "NOT_FOUND"
  if (status === 429) return "RATE_LIMITED"
  if (status === 400 || status === 422) return "VALIDATION"
  if (status >= 500) return "SERVER"
  return "UNKNOWN"
}

/**
 * Endpoints sob os quais o cookie de refresh é visível ao backend. Só neles o app
 * nativo envia o refresh token, para não espalhar a credencial de longa duração por
 * requests que não têm nada a ver com sessão.
 */
function isAuthEndpoint(path: string): boolean {
  return path.startsWith("/api/auth")
}

// ─── core ───────────────────────────────────────────────────────────────────

/**
 * Executa a request usando CapacitorHttp (nativo, sem CORS).
 * Usado apenas quando rodando dentro do app Android/iOS.
 */
async function requestNative(
  url: string,
  method: string,
  headers: Record<string, string>,
  body?: unknown,
): Promise<{ status: number; data: unknown }> {
  const response = await CapacitorHttp.request({
    url,
    method,
    headers,
    data: body !== undefined ? body : undefined,
    responseType: "json",
  })
  return { status: response.status, data: response.data }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  // Falha rápida quando não existe conexão.
  if (isDisconnected()) {
    throw new ApiError(
      "Sem conexão com a internet.",
      "NETWORK",
      0,
    )
  }

  const {
    public: isPublic,
    timeout = DEFAULT_TIMEOUT_MS,
    retries = MAX_RETRIES,
    skipAuthRefresh,
    sessionGuard,
    ...fetchOptions
  } = options

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(fetchOptions.headers as Record<string, string> | undefined ?? {}),
  }

  sessionGuard?.()
  const native = isNativeApp()

  if (native) {
    headers[PLATFORM_HEADER] = NATIVE_PLATFORM

    // Sem cookie confiável no app, o refresh token viaja no header — e somente para
    // os endpoints de autenticação, que são os únicos que o consomem.
    if (isAuthEndpoint(path)) {
      const stored = await token.getRefreshToken()
      if (stored) headers[REFRESH_TOKEN_HEADER] = stored
    }
  }

  if (!isPublic) {
    // Renovação proativa: mais barato do que gastar uma ida ao servidor para
    // descobrir o 401 e depois repetir a request.
    if (!skipAuthRefresh && token.isExpiringSoon(RENEW_BEFORE_EXPIRY_SECONDS)) {
      const outcome = await refreshSession()
      sessionGuard?.()

      if (outcome.status === "rejected") {
        endSession()
        throw new ApiError("Sessão expirada.", "UNAUTHORIZED", 401)
      }
      // `unavailable` segue adiante com o token atual: ele pode ainda ser aceito, e
      // deixar a request acontecer é melhor do que derrubar a sessão por um soluço
      // de rede.
    }

    const t = token.get()
    if (t) {
      headers["Authorization"] = `Bearer ${t}`
    }
  }

  const url = `${BASE_URL}${path.startsWith("/") ? path : `/${path}`}`
  const method = (fetchOptions.method ?? "GET").toUpperCase()

  const dispatch = () =>
    native
      ? dispatchNative<T>(url, method, headers, fetchOptions, retries, isPublic === true, sessionGuard)
      : dispatchWeb<T>(url, headers, fetchOptions, timeout, retries, isPublic === true, sessionGuard)

  try {
    return await dispatch()
  } catch (err) {
    if (
      !(err instanceof ApiError) ||
      err.code !== "UNAUTHORIZED" ||
      isPublic ||
      skipAuthRefresh
    ) {
      throw err
    }

    // O access token foi recusado. Uma única tentativa de renovação e uma única
    // repetição: se o token novo também não servir, insistir só vira laço.
    sessionGuard?.()
    const outcome = await refreshSession()
    sessionGuard?.()

    if (outcome.status === "renewed") {
      headers["Authorization"] = `Bearer ${outcome.accessToken}`
      return await dispatch()
    }

    if (outcome.status === "unavailable") {
      // Não foi possível confirmar se a sessão morreu. Deslogar aqui puniria o usuário
      // por uma instabilidade momentânea da rede ou da API.
      throw new ApiError(
        "Não foi possível confirmar sua sessão. Verifique a conexão e tente novamente.",
        "UNREACHABLE",
        0,
      )
    }

    endSession()
    throw err
  }
}

// ─── Retry loop (app nativo / CapacitorHttp) ────────────────────────────────

async function dispatchNative<T>(
  url: string,
  method: string,
  headers: Record<string, string>,
  fetchOptions: RequestInit,
  retries: number,
  isPublic: boolean,
  sessionGuard?: () => void,
): Promise<T> {
  let lastNativeError: ApiError | undefined

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) {
      await sleep(RETRY_BASE_MS * 2 ** (attempt - 1))
      if (isDisconnected()) throw new ApiError("Sem conexão com a internet.", "NETWORK", 0)
    }

    sessionGuard?.()
    try {
      let bodyData: unknown
      if (fetchOptions.body && typeof fetchOptions.body === "string") {
        try { bodyData = JSON.parse(fetchOptions.body) } catch { bodyData = fetchOptions.body }
      }

      const { status, data } = await requestNative(url, method, headers, bodyData)
      const payload = data as ApiResponse<T> | undefined

      if (status === 401) {
        if (isPublic) {
          throw new ApiError(
            payload?.message ?? "E-mail ou senha inválidos.",
            "INVALID_CREDENTIALS",
            401,
            payload?.errors ?? [],
          )
        }

        // A sessão não é encerrada aqui: quem chamou ainda vai tentar renovar.
        throw new ApiError(
          payload?.message ?? "Sessão expirada.",
          "UNAUTHORIZED",
          401,
          payload?.errors ?? [],
        )
      }

      if (status < 200 || status >= 300 || payload?.success === false) {
        const code = statusToCode(status)
        const apiError = new ApiError(
          payload?.message ?? `Erro ${status}`,
          code,
          status,
          payload?.errors ?? [],
          payload?.errorCode,
        )
        if (apiError.retryable && attempt < retries) { lastNativeError = apiError; continue }
        throw apiError
      }

      return (payload?.data ?? undefined) as T

    } catch (err) {
      if (err instanceof ApiError) {
        if (err.retryable && attempt < retries) { lastNativeError = err; continue }
        throw err
      }
      const networkError = new ApiError(
        "Não foi possível conectar ao servidor.",
        "UNREACHABLE",
        0,
      )
      if (attempt < retries) { lastNativeError = networkError; continue }
      throw networkError
    }
  }

  throw lastNativeError ?? new ApiError("Erro após múltiplas tentativas.", "UNKNOWN")
}

// ─── Retry loop (Web / fetch padrão) ────────────────────────────────────────

async function dispatchWeb<T>(
  url: string,
  headers: Record<string, string>,
  fetchOptions: RequestInit,
  timeout: number,
  retries: number,
  isPublic: boolean,
  sessionGuard?: () => void,
): Promise<T> {
  let lastError: ApiError | undefined

  for (let attempt = 0; attempt <= retries; attempt++) {
    // Backoff entre retries (0ms na 1ª tentativa)
    if (attempt > 0) {
      await sleep(RETRY_BASE_MS * 2 ** (attempt - 1))
      // Re-check online status antes de retry
      if (isDisconnected()) {
        throw new ApiError("Sem conexão com a internet.", "NETWORK", 0)
      }
    }

    sessionGuard?.()
    // AbortController para timeout
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeout)

    try {
      const response = await fetch(url, {
        // O cookie de refresh é HttpOnly e cross-origin; sem `include` ele não
        // acompanha a request e a renovação nunca funcionaria.
        credentials: "include",
        ...fetchOptions,
        headers,
        signal: controller.signal,
      })

      clearTimeout(timer)

      // Parse JSON
      const text = await response.text()
      let payload: ApiResponse<T> | undefined

      try {
        payload = text ? (JSON.parse(text) as ApiResponse<T>) : undefined
      } catch {
        // Resposta sem JSON (ex.: 502/503 do Azure)
      }

      // ── 401: inválido em endpoint público vs token expirado ─────────
      if (response.status === 401) {
        if (isPublic) {
          throw new ApiError(
            payload?.message ?? "E-mail ou senha inválidos.",
            "INVALID_CREDENTIALS",
            401,
            payload?.errors ?? [],
          )
        }

        // A sessão não é encerrada aqui: quem chamou ainda vai tentar renovar.
        throw new ApiError(
          payload?.message ?? "Sessão expirada.",
          "UNAUTHORIZED",
          401,
          payload?.errors ?? [],
        )
      }

      // ── Erro HTTP genérico ──────────────────────────────────────────
      if (!response.ok || payload?.success === false) {
        const code = statusToCode(response.status)
        const apiError = new ApiError(
          payload?.message ?? `Erro ${response.status}: ${response.statusText}`,
          code,
          response.status,
          payload?.errors ?? [],
          payload?.errorCode,
        )

        // Só faz retry em 5xx (erros de servidor transitórios)
        if (apiError.retryable && attempt < retries) {
          lastError = apiError
          continue
        }

        throw apiError
      }

      // ── Sucesso ─────────────────────────────────────────────────────
      return (payload?.data ?? undefined) as T

    } catch (err) {
      clearTimeout(timer)

      // Se já é ApiError, propaga (a menos que seja retryable)
      if (err instanceof ApiError) {
        if (err.retryable && attempt < retries) {
          lastError = err
          continue
        }
        throw err
      }

      // Timeout (AbortError)
      if (err instanceof DOMException && err.name === "AbortError") {
        const timeoutError = new ApiError(
          "Tempo de conexão esgotado.",
          "TIMEOUT",
          0,
        )
        if (attempt < retries) {
          lastError = timeoutError
          continue
        }
        throw timeoutError
      }

      // TypeError do fetch = rede ausente / CORS / DNS / servidor inacessível
      if (err instanceof TypeError) {
        const online = !isDisconnected()
        const networkError = new ApiError(
          online
            ? "Não foi possível conectar ao servidor."
            : "Sem conexão com a internet.",
          online ? "UNREACHABLE" : "NETWORK",
          0,
        )
        if (attempt < retries) {
          lastError = networkError
          continue
        }
        throw networkError
      }

      // Erro inesperado — não faz retry
      throw new ApiError(
        err instanceof Error ? err.message : "Erro inesperado.",
        "UNKNOWN",
      )
    }
  }

  // Se chegou aqui, esgotou os retries
  throw lastError ?? new ApiError("Erro após múltiplas tentativas.", "UNKNOWN")
}

// ─── API pública ────────────────────────────────────────────────────────────

export const http = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { method: "GET", ...options }),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...options,
    }),

  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, {
      method: "PUT",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...options,
    }),

  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, {
      method: "PATCH",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...options,
    }),

  delete: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, {
      method: "DELETE",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...options,
    }),
}
